import React from "react";
import { Box, Typography, Paper, Chip, Tooltip, CircularProgress, Button } from "@mui/material";
import {
  ViewKanban,
  WhatsApp,
  SmartToy,
  PersonOutline,
  ForumOutlined,
  TimerOutlined,
  HourglassBottom,
  Star,
} from "@mui/icons-material";
import { PipelineSummary } from "../../services/crmService";
import { colors, typography } from "../../theme/designSystem";

// Painel de entrada do CRM: um card por pipeline que o usuário acessa, com uso e
// PENDÊNCIAS (o que dói num CRM é família esperando resposta). 4 por linha no desktop.

interface PipelineHubProps {
  summaries: PipelineSummary[];
  loading: boolean;
  onOpen: (pipelineId: string) => void;
}

/** Contador de pendência: some quando zerado, para o card só mostrar o que precisa de ação. */
const Pending: React.FC<{
  icon: React.ReactElement<{ sx?: object }>;
  value: number;
  label: string;
  tone: "alert" | "warn" | "neutral";
}> = ({ icon, value, label, tone }) => {
  if (value === 0) return null;
  const palette = {
    alert: { bg: "#fdecea", fg: "#a4262c" },
    warn: { bg: "#fff3cd", fg: "#856404" },
    neutral: { bg: "#eef0f2", fg: colors.textSecondary },
  }[tone];
  return (
    <Tooltip title={label}>
      <Chip
        size="small"
        icon={React.cloneElement(icon, { sx: { fontSize: 15, color: `${palette.fg} !important` } })}
        label={`${value} ${label}`}
        sx={{
          height: 24,
          fontSize: "0.72rem",
          fontWeight: 600,
          backgroundColor: palette.bg,
          color: palette.fg,
          maxWidth: "100%",
          "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
        }}
      />
    </Tooltip>
  );
};

const PipelineHub: React.FC<PipelineHubProps> = ({ summaries, loading, onOpen }) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (summaries.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "2rem 1.25rem",
          border: "1px solid #e9ecef",
          textAlign: "center",
        }}
      >
        <ViewKanban sx={{ fontSize: 40, color: colors.textMuted, mb: 1 }} />
        <Typography sx={{ fontWeight: typography.fontWeight.semibold, mb: 0.5 }}>
          Você ainda não tem acesso a nenhum pipeline
        </Typography>
        <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
          Peça ao administrador para incluir você nos responsáveis de um pipeline em
          CRM &gt; Configuração de pipeline.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        // 4 por linha no desktop; degrada para 3, 2 e 1 conforme a largura
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(4, 1fr)",
        },
        gap: 2,
      }}
    >
      {summaries.map((pipeline) => {
        const pendencies =
          pipeline.awaitingReply + pipeline.unassigned + pipeline.stale + pipeline.windowClosing;
        return (
          <Paper
            key={pipeline.id}
            elevation={0}
            onClick={() => onOpen(pipeline.id)}
            sx={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "1rem 1.25rem",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
              // borda de atenção quando há pendência real
              border: `1px solid ${pipeline.windowClosing > 0 ? "#f5c6cb" : "#e9ecef"}`,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              transition: "box-shadow 200ms, transform 200ms",
              "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.12)", transform: "translateY(-2px)" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              <ViewKanban sx={{ color: colors.primary, flexShrink: 0 }} />
              <Typography
                sx={{
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.textPrimary,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {pipeline.name}
              </Typography>
              {pipeline.isDefault && (
                <Tooltip title="Pipeline padrão">
                  <Star sx={{ fontSize: 16, color: "#f0ad00", flexShrink: 0 }} />
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
              <Typography
                sx={{ fontSize: "2rem", fontWeight: typography.fontWeight.bold, color: colors.textPrimary, lineHeight: 1 }}
              >
                {pipeline.openLeads}
              </Typography>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                lead{pipeline.openLeads === 1 ? "" : "s"} em aberto
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, minHeight: 24 }}>
              {pendencies === 0 ? (
                <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.success, fontWeight: 600 }}>
                  Sem pendências 🎉
                </Typography>
              ) : (
                <>
                  <Pending
                    icon={<HourglassBottom />}
                    value={pipeline.windowClosing}
                    label="janela 24h acabando"
                    tone="alert"
                  />
                  <Pending
                    icon={<ForumOutlined />}
                    value={pipeline.awaitingReply}
                    label="sem resposta"
                    tone="alert"
                  />
                  <Pending
                    icon={<PersonOutline />}
                    value={pipeline.unassigned}
                    label="sem responsável"
                    tone="warn"
                  />
                  <Pending icon={<TimerOutlined />} value={pipeline.stale} label="parados" tone="neutral" />
                </>
              )}
            </Box>

            <Box sx={{ borderTop: "1px solid #f1f3f5", pt: 0.75, mt: "auto" }}>
              <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                {pipeline.newLast7Days} novo{pipeline.newLast7Days === 1 ? "" : "s"} em 7 dias ·{" "}
                {pipeline.wonLast30Days} ganho{pipeline.wonLast30Days === 1 ? "" : "s"} em 30 dias
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
                <WhatsApp
                  sx={{ fontSize: 14, color: pipeline.channels.length ? colors.whatsapp : colors.textMuted }}
                />
                <Typography
                  sx={{
                    fontSize: typography.fontSize.xs,
                    color: colors.textMuted,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 150,
                  }}
                >
                  {pipeline.channels.length ? pipeline.channels.join(", ") : "sem canal"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                <SmartToy
                  sx={{ fontSize: 14, color: pipeline.agentName ? colors.primary : colors.textMuted }}
                />
                <Typography
                  sx={{
                    fontSize: typography.fontSize.xs,
                    color: pipeline.agentName ? colors.textSecondary : colors.textMuted,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {pipeline.agentName
                    ? `${pipeline.agentName} atende os leads sem responsável`
                    : "Sem agente — atendimento 100% humano"}
                </Typography>
              </Box>
            </Box>

            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(pipeline.id);
              }}
              sx={{ textTransform: "none", color: colors.primary, alignSelf: "flex-start", p: 0, minWidth: 0 }}
            >
              Abrir quadro →
            </Button>
          </Paper>
        );
      })}
    </Box>
  );
};

export default PipelineHub;
